import type { UpcomingVisit } from '@/api/types/ProviderApi'
import { visitTitle } from '@/components/HomePage/HomeTiles/VisitCard'
import { StatusPill } from '@/components/Rows'
import { confirmOpensShort, confirmStateOf } from '@/features/home/confirmation'

/** "Perio Evaluation · 30 min", or just the type until the server sends the duration. */
export const typeLine = (visit: UpcomingVisit) =>
	[visitTitle(visit), visit.durationInMinutes && `${visit.durationInMinutes} min`]
		.filter(Boolean)
		.join(' · ')

/**
 * The state, as a mark in the corner rather than a line in the body.
 *
 * Nothing while the studio is actually asking: the ask is the slot's own button, and a chip
 * saying "confirm" beside a button that confirms is the same instruction twice.
 *
 * Every state takes the same white ground and the same black mark — `neutral` with `onGround`,
 * which is what that pair was written for: "a reader meets one of those a state at a time, days
 * or weeks apart, so mint-for-done against grey-for-waiting never reads as a scale — it reads as
 * two different kinds of object standing in one place". The mint was the one spend of colour on
 * the screen at the moment nothing is being asked of anybody, and it sat on a photograph, where
 * a tint has to fight whatever is behind it.
 */
export const StateChip = ({
	visit,
	onPhoto,
}: { visit: UpcomingVisit; onPhoto?: boolean }) => {
	const state = confirmStateOf(visit)
	if (visit.checkIn?.status === 'EXPIRED') {
		// The card's own way of knowing. Without it Home reads "Confirmed" while the door has
		// quietly shut, which makes it the one screen that does not know what happened.
		return <StatusPill status="wait" label="Check-in Closed" onGround={onPhoto} />
	}
	if (visit.checkIn?.status === 'CHECKED_IN') {
		// The card's corner only. The page draws "You're In" over "We'll come and get you
		// shortly", which is the same fact in the member's own words with what happens next
		// attached — and a capsule reading "Checked In" two inches above it is the app saying it
		// twice, once in a register nobody speaks.
		return onPhoto ? (
			<StatusPill status="done" label="Checked In" neutral onGround />
		) : null
	}
	// Nothing about confirming, where confirming has no consequence: the switch in
	// provider-kapi#1167 releases nobody, so "Not Confirmed" would be a warning about a thing
	// that cannot happen and "Confirmed" a receipt nobody asked for.
	if (visit.confirmationRequired === false) {
		return null
	}
	if (state === 'confirmed') {
		return <StatusPill status="done" label="Confirmed" neutral onGround />
	}
	if (state === 'early') {
		// On the photograph only. The visit page says this in a row with the date the window
		// opens in it — see `visitStateRow` — and a capsule at the heading's right margin saying
		// the same two words is the state told twice, in the smaller of the two places. The card
		// has no room for a row, so there the chip is still the only thing that can carry it.
		return onPhoto ? (
			<StatusPill status="wait" label={confirmOpensShort()} onGround />
		) : null
	}
	return null
}
